import { useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import _uniqueId from 'lodash/uniqueId'

import type { CreateProductType, ProductFormType } from '@src/type/product/form'

import useUploadFile from '@src/hooks/queries/file/useUploadFile'
import useCreateVendorProductQuery from '@src/hooks/queries/product/useCreateVendorProductQuery'

import { CenterModalContext } from '@src/contexts/popup/modal/CenterModalProvider'

const useMyProductForm = () => {
	const navigate = useNavigate()

	const { openCenterModal, closeCenterModal } = useContext(CenterModalContext)
	const { mutate: createVendorProduct } = useCreateVendorProductQuery()
	const { mutateAsync: uploadFileMutate } = useUploadFile()

	const productFormSchema = Yup.object({
		title: Yup.string().required('이름을 정해주세요'),
		isOptionExist: Yup.boolean(),
		price: Yup.number().when('isOptionExist', {
			is: false,
			then: (schema) =>
				schema
					.typeError('숫자를 입력해주세요')
					.min(1, '최소 숫자는 1 입니다.')
					.required('가격을 설정해 주세요.'),
			otherwise: (schema) =>
				schema
					.transform((_, val: unknown) => (val === Number(val) ? val : null))
					.nullable(true)
					.optional(),
		}),
		challengePrice: Yup.number().when('isOptionExist', {
			is: false,
			then: (schema) =>
				schema
					.typeError('숫자를 입력해주세요')
					.min(1, '최소 숫자는 1 입니다.')
					.required('가격을 설정해 주세요.'),
			otherwise: (schema) =>
				schema
					.transform((_, val: unknown) => (val === Number(val) ? val : null))
					.nullable(true)
					.optional(),
		}),
		type: Yup.boolean(),
		challenge: Yup.object({
			title: Yup.string()
				.min(0, '투두리스트 이름을 기입해주세요')
				.required('투두리스트 이름을 기입해주세요'),
			minAuth: Yup.number().typeError('숫자를 입력해주세요').min(1, '최소 숫자는 1 입니다.'),
			period: Yup.number().typeError('숫자를 입력해주세요').min(1, '최소 숫자는 1 입니다.'),
		}),
		shipping: Yup.object({
			courier: Yup.object().required('배송사를 기입해주세요'),
			shippingDay: Yup.number().typeError('숫자를 입력해주세요').min(0, '최소 숫자는 0 입니다.'),
			shippingMsg: Yup.string()
				.min(1, '배송 안내문을 기입해주세요.')
				.required('배송 안내문을 기입해주세요.'),
			price: Yup.number().typeError('숫자를 입력해주세요').min(0, '최소 숫자는 0 입니다.'),
			otherPrice: Yup.number().typeError('숫자를 입력해주세요').min(0, '최소 숫자는 0 입니다.'),
			jejuPrice: Yup.number().typeError('숫자를 입력해주세요').min(0, '최소 숫자는 0 입니다.'),
			refundPrice: Yup.number().typeError('숫자를 입력해주세요').min(0, '최소 숫자는 0 입니다.'),
			refundMsg: Yup.string()
				.min(1, '환불 규정 안내문을 기입해주세요.')
				.required('환불 규정 안내문을 기입해주세요.'),
		}),
		productVariants: Yup.array().when('isOptionExist', {
			is: true,
			then: (schema) =>
				schema.of(
					Yup.object({
						name: Yup.string().required('이름을 설정해주세요'),
					}),
				),
		}),
		productVariantOptions: Yup.array().when('isOptionExist', {
			is: true,
			then: (schema) =>
				schema.of(
					Yup.object({
						name: Yup.string().required('이름을 설정해주세요'),
					}),
				),
		}),
		productSkus: Yup.array().when('isOptionExist', {
			is: true,
			then: (schema) =>
				schema.of(
					Yup.object({
						price: Yup.number()
							.typeError('가격을 설정해 주세요.')
							.min(1, '최소 숫자는 1 입니다.')
							.required('가격을 설정해 주세요.'),
						challengePrice: Yup.number()
							.typeError('가격을 설정해 주세요.')
							.min(1, '최소 숫자는 1 입니다.')
							.required('가격을 설정해 주세요.'),
					}),
				),
		}),
		bannerFile: Yup.mixed().required('파일을 선택해주세요'),
		descriptionFiles: Yup.array()
			.of(Yup.mixed().required('파일을 선택해주세요'))
			.min(1, '파일을 선택해주세요'),
		sampleProofFiles: Yup.array()
			.of(Yup.mixed().required('파일을 선택해주세요'))
			.min(1, '파일을 선택해주세요'),
		bannerBase64: Yup.string(),
	}).required()

	const methods = useForm<ProductFormType>({
		defaultValues: {
			productVariants: [{ name: '', depth: 1 }],
			productVariantOptions: [{ name: '', cno: _uniqueId('variant-option-'), variantDepth: 1 }],
			isOptionExist: false,
			descriptionFiles: [],
			sampleProofFiles: [],
			shipping: {
				price: 3000,
				jejuPrice: 3000,
				otherPrice: 5000,
				refundPrice: 5000,
				shippingDay: 3,
				shippingMsg: `도서산간 추가 배송비\n- 제주지역 : 3,000원\n- 도서산간 지역 : 5,000원`,
				refundMsg: `- 단, 고객 단순 변심의 경우에만 발생\n- 도서산간 및 일부 지역 추가비용 발생`,
			},
			challenge: {
				period: 14,
				minAuth: 8,
			},
		},
		resolver: yupResolver(productFormSchema),
		mode: 'onTouched',
	})

	const handleSubmit = async (data: ProductFormType) => {
		const {
			bannerFile,
			descriptionFiles,
			sampleProofFiles,
			title,
			challengePrice,
			price,
			challenge,
			shipping,
			isOptionExist,
			stockExist,
			stock,
			productVariants,
			productVariantOptions,
			productSkus,
		} = data

		let formData = null

		let bannerArr = null
		if (bannerFile) {
			formData = new FormData()
			formData.append('file', bannerFile)
			formData.append('path', 'commerce/product/banner_img/')
			const uploadRes = await uploadFileMutate(formData)
			bannerArr = uploadRes.data.upload_results.map((result) => {
				return result.file_name
			})
		}

		let descriptionsArr = null
		if (descriptionFiles?.length > 0) {
			formData = new FormData()
			for (let i = 0; i < descriptionFiles.length; i += 1) {
				formData.append('file', descriptionFiles[i])
			}

			formData.append('path', 'commerce/product/description_img/')
			const uploadRes = await uploadFileMutate(formData)
			descriptionsArr = uploadRes.data.upload_results.map((result) => {
				return { filename: result.file_name, type: 'image' }
			})
		}

		let sampleProofArr = null
		if (sampleProofFiles.length > 0) {
			formData = new FormData()
			for (let i = 0; i < sampleProofFiles.length; i += 1) {
				formData.append('file', sampleProofFiles[i])
			}

			formData.append('path', 'commerce/product/sample_proof_img/')
			const uploadRes = await uploadFileMutate(formData)
			sampleProofArr = uploadRes.data.upload_results.map((result) => {
				return result.file_name
			})
		}

		let lowestPrice = price
		let lowestChallengePrice = challengePrice
		if (isOptionExist && Array.isArray(productSkus) && productSkus.length > 0) {
			const lowestPriceSku = productSkus.reduce((prev, curr) => {
				return prev.price < curr.price ? prev : curr
			})
			lowestPrice = lowestPriceSku.price
			const lowestChallengePriceSku = productSkus.reduce((prev, curr) => {
				return prev.challengePrice < curr.challengePrice ? prev : curr
			})
			if (lowestChallengePriceSku.challengePrice) {
				lowestChallengePrice = lowestChallengePriceSku.challengePrice
			}
		}
		if (bannerArr && descriptionsArr && sampleProofArr) {
			const form: CreateProductType = {
				title,
				type: isOptionExist ? 'variants' : 'normal',
				banner_img: bannerArr,
				sample_proof: sampleProofArr,
				descriptions: descriptionsArr,
				lowest_price: lowestPrice,
				lowest_challenge_price: lowestChallengePrice,
				product_policy: {
					refund_policy: shipping.refundMsg,
					shipping_policy: shipping.shippingMsg,
				},
				product_challenge: {
					title,
					category_no: [2],
					product_challenge_tasks: [
						{
							title: challenge.title,
							period: challenge.period,
							min_auth: challenge.minAuth,
							day: ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'],
						},
					],
				},
				product_shipping: {
					courier_no: shipping.courier.value,
					price: shipping.price,
					refund_price: shipping.refundPrice,
					jeju_price: shipping.jejuPrice,
					other_price: shipping.otherPrice,
					estimated_delivery_date: shipping.shippingDay,
				},
				product_variants: !isOptionExist
					? undefined
					: productVariants.slice().map((variant) => {
							return {
								...variant,
								product_variant_options: productVariantOptions
									.filter((option) => {
										return variant.depth === option.variantDepth
									})
									.map((option) => {
										return {
											cno: option.cno,
											name: option.name,
										}
									}),
							}
					  }),
				product_skus:
					isOptionExist === false
						? [
								{
									price,
									challenge_price: challengePrice,
									stock: stockExist ? stock : null,
									cno: [],
								},
						  ]
						: productSkus.map((sku) => {
								return {
									price: sku.price,
									challenge_price: sku.challengePrice,
									stock: !stockExist && !sku.stock ? null : sku.stock,
									cno: sku.cno,
								}
						  }),
			}
			createVendorProduct(form, {
				onSuccess: (resData) => {
					if (resData.success !== 1) {
						openCenterModal({ desc: '상품 생성에 실패했어요. 다시 시도해주세요' }, closeCenterModal)
					} else {
						navigate('/product')
					}
				},
				onError: () => {
					openCenterModal({ desc: '상품 생성에 실패했어요. 다시 시도해주세요' }, closeCenterModal)
				},
			})
		}
	}
	const handleError = (err: unknown) => {
		console.log('err')
		console.log(err)
	}

	return {
		methods,
		handleSubmit: methods?.handleSubmit(handleSubmit, handleError),
	}
}

export default useMyProductForm
